import { Injectable } from '@angular/core';
import { catchError, map, Observable, of } from "rxjs";
import { HttpClient } from '@angular/common/http';
import { ApiService } from './api.service';
import { Device, DeviceCheckIn } from '@core/models/domain/device.domain';
import { DeviceCheckInResponse, DeviceResponse } from '@core/models/response/device.response';
import { buildApiEndpoint, handleError } from '@core/utils/service';
import { buildDeviceFromResponse } from '@core/converter/device.converter';
import { DigiResponse } from '@core/models/response/core';
import { DeviceRegistrationRequest } from '@core/models/request/device.request';

/**
 * DeviceService
 *
 * This service provides methods for managing devices, including loading, registering, and removing devices.
 * It facilitates communication with the backend for device-related operations.
 *
 * Key Features:
 * - Loads all devices associated with the school account.
 * - Registers new devices by sending requests to the server.
 * - Removes devices by sending delete requests to the server.
 */

@Injectable({
  providedIn: "root",
})
export class DeviceService {
  API_URI: string;

  constructor(private http: HttpClient, private apiService: ApiService) {
    this.API_URI = this.apiService.getApiURL("DeviceService", "school-account");
  }

  /**
   * Loads all devices by sending a GET request to the server.
   *
   * This function retrieves a list of devices from the server by making a GET request to the `get-devices` endpoint.
   * The response is mapped to an array of `Device` objects using the `buildDeviceFromResponse` converter.
   * Logs a warning if the response body is invalid or not an array, and returns an empty array in such cases.
   *
   * @returns {Observable<Device[]>} An observable that emits an array of `Device` objects or an empty array in case of an invalid response.
   */
  loadAllDevices(): Observable<Device[]> {
    return this.http
      .get<DigiResponse<DeviceResponse[]>>(buildApiEndpoint(this.API_URI, "get-devices"), { observe: 'response' })
      .pipe(
        map((response) => {
          const loqtResponse = response.body!;
          if (!loqtResponse.body || !Array.isArray(loqtResponse.body)) {
            console.warn("Invalid devices response", loqtResponse.body);
            return [];
          }
          console.log("Devices loaded successfully:", loqtResponse.body);
          return loqtResponse.body.map(buildDeviceFromResponse);
        }),
        catchError((error) => handleError<Device[]>("Loading devices", error))
      );
  }

  /**
   * When each pupil device last checked in (DIGI-1700). Devices that never reported are absent.
   *
   * Best-effort: the column it feeds is extra information, so a failed load is logged and answers
   * an empty list rather than an error that could take the device table down with it.
   */
  loadDeviceCheckIns(): Observable<DeviceCheckIn[]> {
    return this.http
      .get<DigiResponse<DeviceCheckInResponse[]>>(buildApiEndpoint(this.API_URI, "get-device-check-ins"))
      .pipe(
        map((response) => (Array.isArray(response?.body) ? response.body : [])
          .filter(checkIn => !!checkIn?.lastHeartbeatAt)
          .map(checkIn => ({
            accountId: checkIn.accountId,
            serialNumber: checkIn.serialNumber,
            lastHeartbeatAt: new Date(checkIn.lastHeartbeatAt!)
          }))),
        catchError((error) => {
          console.warn("Loading device check-ins failed", error);
          return of([]);
        })
      );
  }

  /**
   * Registers devices by sending a POST request to the server.
   *
   * This function sends a POST request to the `register-devices` endpoint with the provided device registration data.
   * If the operation is successful, the response is mapped to an array of device IDs.
   * Logs the registered device IDs and throws an error if no data is found in the response.
   *
   * @param {DeviceRegistrationRequest} request - The request object containing the device registration data.
   * @returns {Observable<string[]>} An observable that emits an array of registered device IDs or throws an error if the operation fails.
   */
  registerDevices(request: DeviceRegistrationRequest): Observable<Device[]> {
    return this.http
      .post<DigiResponse<DeviceResponse[]>>(buildApiEndpoint(this.API_URI, "register-devices"), request, { observe: 'response' })
      .pipe(
        map((response) => {
          const loqtResponse = response.body!;
          if (loqtResponse.statusCode.status === "OK" && loqtResponse.body) {
            console.log("Device requests added successfully:", loqtResponse.body);
            return loqtResponse.body.map(buildDeviceFromResponse);
          }
          throw new Error("No device data found in response");
        }),
        catchError((error) => handleError<string[]>("Adding device requests", error))
      );
  }

  /**
   * Removes a device by sending a DELETE request to the server.
   *
   * This function sends a DELETE request to the `remove-device` endpoint with the specified device ID.
   * If the operation is successful, the response is mapped to the device ID.
   * Logs the removed device ID and throws an error if no data is found in the response.
   *
   * @param {string} deviceId - The ID of the device to be removed.
   * @returns {Observable<string>} An observable that emits the removed device ID or throws an error if the operation fails.
   */
  removeDevice(deviceId: string): Observable<string> {
    return this.http
      .delete<DigiResponse<string>>(buildApiEndpoint(this.API_URI, `remove-device/${deviceId}`), { observe: 'response' })
      .pipe(
        map((response) => {
          const loqtResponse = response.body!;
          if (loqtResponse.statusCode.status === "OK" && loqtResponse.body) {
            console.log("Device removed successfully:", loqtResponse.body);
            return loqtResponse.body;
          }
          throw new Error("No deivce data found in response");
        }),
        catchError((error) => handleError<string>("Removing device", error))
      );
  }

  /**
   * Removes one of a pupil's phones from LOQT (DIGI-1871), for a lost or broken phone the parent
   * cannot remove because it is the pupil's only one. Unlike `removeDevice`, which only takes a
   * phone off the school's register, this unenrols it: the phone gives LOQT's permissions back and
   * the pupil's parents are told. School admins only; the backend checks it.
   *
   * @param pupilAccountId the pupil's account id
   * @param deviceSerialNumber the phone's serial number
   * @returns an observable that completes when the phone was removed, and errors with the
   *          backend's reason when it was refused
   */
  removePupilDevice(pupilAccountId: string, deviceSerialNumber: string): Observable<void> {
    return this.http
      .post<DigiResponse<void>>(buildApiEndpoint(this.API_URI, "remove-pupil-device"),
        { childAccountId: pupilAccountId, deviceSerialNumber })
      .pipe(
        map((response) => {
          if (response?.statusCode?.status !== "OK") {
            throw new Error(response?.statusCode?.reason || "Removing the phone was refused");
          }
        }),
        catchError((error) => handleError<void>("Removing a pupil's phone", error))
      );
  }
}
